/** Typed env-config object. Validated at startup so missing vars fail fast. */
import { z } from 'zod';

const ConfigSchema = z.object({
  apiBaseUrl: z.string().url(),
  frontendBaseUrl: z.string().url(),
  sentryDsn: z.string().optional(),
  sentryEnv: z.enum(['development', 'staging', 'production']),
  muiXLicenseKey: z.string().optional(),
  // Optional: the fleet Vehicles map falls back to a "not configured" notice when
  // absent, so a missing key must not fail-fast the whole app.
  googleMapsApiKey: z.string().optional(),
  // Django admin origin (audit "open in admin" links). Defaults to the API origin
  // — the monolith serves /admin/ on the same host as /v2/console.
  djangoAdminBaseUrl: z.string().url(),
  // Base for image path chunks — backend serves `{p, s}` refs (e.g. trip-type
  // images) that compose to `${awsImageBaseUrl}${p}${s}`. Optional: absent →
  // images render as the "None" placeholder rather than fail-fast the app.
  awsImageBaseUrl: z.string().optional(),
});

export type Config = z.infer<typeof ConfigSchema>;

function safeOrigin(url: string | undefined): string | undefined {
  try {
    return url ? new URL(url).origin : undefined;
  } catch {
    return undefined;
  }
}

function readEnv(): Config {
  const raw = {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
    frontendBaseUrl: import.meta.env.VITE_FRONTEND_BASE_URL,
    sentryDsn: import.meta.env.VITE_SENTRY_DSN || undefined,
    sentryEnv: import.meta.env.VITE_SENTRY_ENV,
    muiXLicenseKey: import.meta.env.VITE_MUI_X_LICENSE_KEY || undefined,
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || undefined,
    djangoAdminBaseUrl:
      import.meta.env.VITE_DJANGO_ADMIN_BASE_URL ||
      safeOrigin(import.meta.env.VITE_API_BASE_URL),
    awsImageBaseUrl: import.meta.env.VITE_AWS_IMAGE_BASE_URL || undefined,
  };
  const parsed = ConfigSchema.safeParse(raw);
  if (!parsed.success) {
    // Surface a clear error rather than letting downstream code crash on undefined.
    // eslint-disable-next-line no-console
    console.error('Invalid env config:', parsed.error.flatten());
    throw new Error('Invalid env config — see console for details.');
  }
  return parsed.data;
}

export const config: Config = readEnv();
